/* eslint-disable no-nested-ternary */
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faPencil } from "@fortawesome/free-solid-svg-icons/faPencil";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faSave } from "@fortawesome/free-solid-svg-icons/faSave";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { Tooltip } from "../../../utils/local-antd";
import type { MenuProps } from "../../../utils/local-antd";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { FontSize, Text } from "../../../components/Text";
import { SortableChild } from "../../../components/dnd/SortableChild";
import { KanbanColumn as KanbanColumnModel } from "../../../model/kanban/KanbanColumn";
import { UpdateKanbanColumnDto } from "../../../model/kanban/dto/UpdateKanbanColumnDto";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { getTranslatedKanbanColumnName } from "../KanbanUtils";
import { KanbanCard } from "./KanbanCard";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Shadow } from "../../../theme/Shadow";
import i18n from "../../../i18n";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { getCurrencySymbol } from "../../../utils/pricingUtils";

const StyledKanbanColumn = styled(Flex)`
  direction: ${i18n.language === AppLangs.AR ? "rtl" : "ltr"};
  width: 18rem;
  height: 100%;
  flex-direction: column;
  margin-inline-end: ${Spacing.m};
  border-radius: ${Rounded.lg};
  border: 1px solid ${({ theme }) => theme.darkSub};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
  overflow: hidden;
`;

const ColumnHeader = styled(Flex)<{ isDefault: boolean }>`
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.s};
  cursor: grab;
  background-color: ${({ isDefault, theme }) =>
    isDefault ? theme.darkSub : theme.darkMain};
  color: ${({ isDefault, theme }) =>
    isDefault ? theme.textColor : theme.white};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};
  * {
    color: inherit;
  }
`;

const KanbanCardsWrapper = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) => theme.sub};
`;

const KanbanCradsInnerWrapper = styled(Flex)<{ canAddCard: boolean }>`
  height: ${({ canAddCard }) => (canAddCard ? "calc(100% - 3rem)" : "100%")};
  flex-direction: column;
  gap: ${Spacing.s};
  overflow-y: auto;

  padding-bottom: ${Spacing.s};

  scrollbar-color: ${({ theme }) => `${theme.main} ${theme.sub}`};
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.sub};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.main};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.lightMain};
  }
`;

const AddKanbanCardButton = styled(Button)`
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;

  width: 100%;
  height: 3rem;

  border-radius: ${Rounded.none};
  margin: 0;

  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.main};
  border-top: 1px solid ${({ theme }) => theme.darkSub};

  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};

  &:hover {
    background-color: ${({ theme }) => theme.lightSub};
    color: ${({ theme }) => theme.main};
  }

  p {
    color: inherit;
  }
`;

const ColumnHeaderTitleInput = styled(Input)<{ $canUpdateColName: boolean }>`
  color: ${({ theme }) => theme.main};
  .atira-input-wrapper {
    gap: 0;
  }

  .atira-input-wrapper input {
    font-size: ${FontSize.l};
    font-weight: 700;
  }

  cursor: ${({ $canUpdateColName }) => ($canUpdateColName ? "text" : "grab")};
  border: ${({ $canUpdateColName, theme }) =>
    $canUpdateColName ? `1px solid ${theme.main}` : "none"} !important;
  transition: all 0.2s;
  padding: ${({ $canUpdateColName }) =>
    $canUpdateColName ? `${Spacing.none} ${Spacing.s}` : Spacing.s} !important;

  html[dir="rtl"] & {
    direction: rtl;
  }

  html[dir="ltr"] & {
    direction: ltr;
  }
`;

const ColumnNameEditButton = styled(Button)`
  margin: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
`;

const MenuButton = styled(Button)`
  margin: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
`;

const MenuActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  justify-content: space-between;
  margin: 0;
  padding: ${Spacing.xs};
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
  padding: 0.2rem;
  width: 100%;
`;

const CancelRenameColumnButton = styled(ColumnNameEditButton)``;

const AutomationRulesBadge = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin: 0;
  padding: 0 ${Spacing.s};
  border: 1px solid ${({ theme }) => theme.main};
  border-radius: ${Rounded.lg};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.main};
  font-size: ${FontSize.s};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

type KanbanColumnProps = {
  column: KanbanColumnModel;
  onMove?: ATVoidFunction<"left" | "right">;
  isLast?: boolean;
};

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  isLast,
  onMove,
}) => {
  const [canUpdateColName, setCanUpdateColName] = useState(false);
  const [updateColumnNameLoading, setUpdateColumnNameLoading] = useState(false);

  const ref = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const currentKanban = useAppSelector(
    kanbanSliceSelectors.selectUserCurrentKanban,
  );
  const kanbanLoading = useAppSelector(
    kanbanSliceSelectors.selectUserKanbanLoading,
  );
  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.permissions.permissions;

  const userCurrency = user?.settings?.currency || "USD";

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UpdateKanbanColumnDto>({
    mode: "all",
  });

  const {
    setKanbanCardCreateVisible,
    setSelectedKanbanColumnId,
    setKanbanAutomationVisible,
  } = useDrawersContext();

  const dispatch = useAppDispatch();

  const rulesTotal = column.automationRules?.length ?? 0;

  const onStartEditName = () => {
    setCanUpdateColName(true);
    if (ref.current) {
      ref.current.focus();
    }
  };

  const onOpenCreateKanbanCard = () => {
    setSelectedKanbanColumnId(column._id);
    setKanbanCardCreateVisible(true);
  };

  const items: MenuProps["items"] = [
    {
      key: "g1",
      type: "group",
      children: [
        {
          key: "rename",
          label: (
            <MenuActionButton onClick={onStartEditName}>
              {t("common.rename")}
              <AtiraIcon icon={faPencil} />
            </MenuActionButton>
          ),
        },
        {
          key: "automation",
          label: (
            <MenuActionButton onClick={() => setKanbanAutomationVisible(true)}>
              {t("kanban.automation.menu.item")}
              <AtiraIcon icon={faBolt} />
            </MenuActionButton>
          ),
        },
      ],
    },
    {
      key: "sperator",
      type: "divider",
    },
    {
      key: "g2",
      type: "group",
      children: [
        {
          key: "1",
          disabled: isLast === true,
          label: (
            <MenuActionButton
              onClick={() =>
                onMove?.(i18n.language === AppLangs.AR ? "left" : "right")
              }
            >
              {i18n.language === AppLangs.AR
                ? t("deals.column.move_left")
                : t("deals.column.move_right")}
              <AtiraIcon
                icon={
                  i18n.language === AppLangs.AR ? faArrowLeft : faArrowRight
                }
              />
            </MenuActionButton>
          ),
        },
        {
          key: "2",
          disabled: column.order === 0,
          label: (
            <MenuActionButton
              onClick={() =>
                onMove?.(i18n.language === AppLangs.AR ? "right" : "left")
              }
            >
              {i18n.language === AppLangs.AR
                ? t("deals.column.move_right")
                : t("deals.column.move_left")}
              <AtiraIcon
                icon={
                  i18n.language === AppLangs.AR ? faArrowRight : faArrowLeft
                }
              />
            </MenuActionButton>
          ),
        },
      ].filter((item) => !item.disabled),
    },
  ];

  const canAddCard =
    column.default !== true && userPermissions?.KANBAN_CARD.CREATE !== false;

  const totalAmount =
    column?.cards
      ?.map((card) => Number(card?.amount || 0))
      ?.reduce((total, acc) => total + acc, 0) || 0;

  const onCancelEditName = () => {
    setCanUpdateColName(false);
    setValue("name", column.name);
    if (ref.current) {
      ref.current.blur();
    }
  };

  const onEditColumnName = async () => {
    try {
      const dto = getValues();

      if (dto.name !== column.name) {
        setUpdateColumnNameLoading(true);
        await dispatch(kanbanActions.updateKanbanColumnById(dto)).unwrap();
      }

      AtiraToast.success(t("kanban.column.title.update.onsucess"));

      setCanUpdateColName(false);
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setUpdateColumnNameLoading(false);
    }
  };

  useEffect(() => {
    reset({
      name: getTranslatedKanbanColumnName(column.name, currentKanban?.template),
      userId,
      columnId: column._id,
      kanbanId: currentKanban?._id,
    });
  }, [
    reset,
    column.name,
    column._id,
    currentKanban?._id,
    currentKanban?.template,
    userId,
  ]);

  return (
    <StyledKanbanColumn>
      <SortableChild
        style={{ opacity: kanbanLoading ? 0.5 : 1 }}
        id={column._id}
        key={column._id}
        disabled={userPermissions?.KANBAN.UPDATE !== true || kanbanLoading}
      >
        <ColumnHeader isDefault={column.default === true}>
          <Flex alignItems="center" width={"100%"} gap="s">
            <Controller
              control={control}
              name="name"
              rules={{ required: true, minLength: 3, maxLength: Lengths.NAME }}
              render={({ field: { value, onChange } }) => (
                <ColumnHeaderTitleInput
                  readOnly={!canUpdateColName}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                  $canUpdateColName={canUpdateColName}
                  margin={Spacing.none}
                />
              )}
            />

            {userPermissions?.KANBAN.UPDATE ? (
              canUpdateColName ? (
                <Flex alignItems="center" gap="s">
                  <ColumnNameEditButton
                    loading={updateColumnNameLoading}
                    icon={faSave}
                    iconWidth="lg"
                    padding="xs"
                    onClick={handleSubmit(onEditColumnName)}
                    disabled={errors.name ? true : false}
                  />

                  <CancelRenameColumnButton
                    icon={faXmark}
                    padding="xs"
                    iconWidth="lg"
                    onClick={onCancelEditName}
                  />
                </Flex>
              ) : (
                <AtiraDropdown items={items}>
                  <MenuButton icon={faEllipsis} padding="xs" iconWidth="xl" />
                </AtiraDropdown>
              )
            ) : null}
          </Flex>

          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={"100%"}
          >
            <Text align="center">
              {column.cards?.length} {t("common.deals")}
            </Text>
            {rulesTotal ? (
              <Tooltip
                title={t("kanban.automation.lane.rulesTotal", {
                  total: rulesTotal,
                })}
              >
                <AutomationRulesBadge
                  type="button"
                  disabled={userPermissions?.KANBAN.UPDATE !== true}
                  aria-label={t("kanban.automation.lane.rulesTotal", {
                    total: rulesTotal,
                  })}
                  onClick={() => setKanbanAutomationVisible(true)}
                >
                  <AtiraIcon icon={faBolt} size="sm" />
                  {rulesTotal}
                </AutomationRulesBadge>
              </Tooltip>
            ) : null}
            <Text>
              {totalAmount}
              {getCurrencySymbol(userCurrency)}
            </Text>
          </Flex>
        </ColumnHeader>
      </SortableChild>

      <KanbanCardsWrapper height={"100%"}>
        <KanbanCradsInnerWrapper canAddCard={canAddCard}>
          {userPermissions?.KANBAN_CARD.READ ? (
            column.cards?.map((c) => (
              <KanbanCard card={c} column={column} key={c._id} />
            ))
          ) : (
            <AtiraEmpty description={t("common.nothing")} />
          )}
        </KanbanCradsInnerWrapper>

        {canAddCard ? (
          <AddKanbanCardButton
            onClick={onOpenCreateKanbanCard}
            dataTestId={TestIDs.kanbans.createEdit.column.button(column._id)}
          >
            <AtiraIcon icon={faPlus} size="1x" />
            <Text fontSize="m">{t("deals.drawer.create.title")}</Text>
          </AddKanbanCardButton>
        ) : null}
      </KanbanCardsWrapper>
    </StyledKanbanColumn>
  );
};
